import React from 'react';
import MuiButton from '@material-ui/core/Button';
import './Button.less';
import { forwardRef, useCallback } from 'react';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import { ButtonBaseActions } from '@material-ui/core';
import classNames from 'classnames';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'link' | 'material';
export type ButtonFontSize = 'extra-small' | 'small' | 'normal';
export type ButtonFontWeight = 'normal' | 'medium' | 'bold';
export type IButtonProps = Omit<React.ComponentPropsWithRef<'button'>, 'color'> & {
  /**
   * Determines button style
   */
  variant: ButtonVariant;
  /**
   * Disables button and displays loading indicator
   */
  loading?: boolean;
  /**
   * Represents invalid button state.
   *
   * TODO: currently we don't have a use case for this property. We might will need to pass array of ValidationIssues instead of it.
   */
  invalid?: boolean;
  /**
   * Allows to use button as a link
   */
  href?: string;
  /**
   * @default 'uppercase'
   */
  textTransform?: 'capitalize' | 'uppercase' | 'lowercase' | 'initial';
  /**
   * @default 'normal'
   */
  fontSize?: ButtonFontSize;
  /**
   * @default 'bold'
   */
  fontWeight?: ButtonFontWeight;
  /**
   * Indicates that button opened dropDown and applies corresponding styles for the button. Currently this property is supported only for link button with dropdown.
   */
  dropDownOpened?: boolean;
} & (
    | {
        /**
         * Determines button style
         */
        variant: 'material';
        /**
         * Dropdown is not supported for material-ui button currently
         */
        dropdown?: never;
      }
    | {
        /**
         * Determines button style
         */
        variant: Exclude<ButtonVariant, 'material'>;
        /**
         * Displays chevron next to the button text
         */
        dropdown?: boolean;
      }
  );

const getClassName = (
  variant: ButtonVariant,
  fontSize: string,
  fontWeight: string,
  disabled?: boolean,
  loading?: boolean,
  invalid?: boolean,
  dropDownOpened?: boolean,
  className?: string
) =>
  classNames(
    'dpc-button',
    `dpc-button--${variant}`,
    `dpc-button--size-${fontSize}`,
    `dpc-button--weight-${fontWeight}`,
    className,
    {
      'dpc-button--disabled': disabled,
      'dpc-button--loading': loading,
      'dpc-button--invalid': invalid,
      'dpc-button--dropdown-opened': dropDownOpened,
    }
  );

/**
 * Button component
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export const Button = forwardRef(
  (
    {
      variant = 'primary',
      children,
      className,
      disabled,
      loading,
      invalid,
      dropdown,
      textTransform = 'uppercase',
      fontSize = 'normal',
      fontWeight = 'bold',
      dropDownOpened,
      ...otherProps
    }: IButtonProps,
    ref?: React.Ref<HTMLButtonElement>
  ) => {
    disabled = disabled || loading;

    /**
     * When we set focus it does not get focused initially so to get ripple effect initially we are handling focus here where action is focus visible.
     */
    const action = React.createRef<ButtonBaseActions>();
    const handleFocus = useCallback(
      (event: React.FocusEvent<HTMLButtonElement>) => {
        action.current?.focusVisible();
        otherProps.onFocus && otherProps.onFocus(event);
      },
      [action, otherProps]
    );

    return (
      <MuiButton
        ref={ref}
        className={getClassName(variant, fontSize, fontWeight, disabled, loading, invalid, dropDownOpened, className)}
        disabled={disabled}
        aria-invalid={invalid}
        color={variant === 'material' ? 'primary' : undefined}
        style={{ textTransform }}
        endIcon={dropdown ? <KeyboardArrowDown viewBox="0 0 14 8" className="dpc-button__arrow" /> : undefined}
        {...otherProps}
        action={action}
        onFocus={handleFocus}
      >
        <span className="dpc-button__text">{children}</span>
      </MuiButton>
    );
  }
);
